import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { User, Search, X, Tag, Loader2 } from "lucide-react";

// Aranabilir müşteri seçici — 10.000+ müşteride tüm listeyi yüklemeden sunucu tarafı arama (/customers?q=).
// Dışarıdan (ör. "Cari" araç butonu) `focusSearch()` ile aramaya odaklanır; müşteri seçiliyse çipi vurgular.
const CustomerSearchSelect = forwardRef(function CustomerSearchSelect({ selected, onSelect, disabled }, ref) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [flash, setFlash] = useState(false);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focusSearch() {
      if (selected) {
        // Zaten seçili → değiştirmek için çipi belirgin vurgula (temizle ile yeni arama)
        setFlash(true);
        setTimeout(() => setFlash(false), 1500);
      } else {
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 60);
      }
    },
  }), [selected]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : "";
        const { data } = await api.get(`/customers${params}`);
        setResults((data || []).slice(0, 30));
      } catch (e) { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (selected) {
    return (
      <div data-testid="pos-customer-chip"
        className={`mt-2 flex items-center gap-2 rounded-md border px-3 py-2 transition-all ${flash ? "border-brand-green bg-brand-green/10 ring-2 ring-brand-green animate-pulse" : "border-brand-green/40 bg-brand-green/5"}`}>
        <User className="h-4 w-4 shrink-0 text-brand-green" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-800">{selected.name}{selected.type === "corporate" ? " (Kurumsal)" : ""}</p>
          {selected.phone && <p className="truncate text-[11px] text-slate-500">{selected.phone}</p>}
        </div>
        {selected.can_wholesale && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-orange/10 px-1.5 py-0.5 text-[10px] font-bold text-brand-orange"><Tag className="h-3 w-3" /> Toptan</span>}
        <button data-testid="pos-customer-clear" onClick={() => onSelect(null)} title="Cariyi kaldır / değiştir" className="shrink-0 rounded-sm p-1 text-slate-400 hover:bg-slate-100 hover:text-red-500"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative mt-2">
      <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <Input data-testid="pos-customer-search" ref={inputRef} value={q} disabled={disabled}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        placeholder="Müşteri ara…" className="h-9 pl-8" />
      {open && (
        <div data-testid="pos-customer-results" className="absolute z-30 mt-1 max-h-72 w-full overflow-y-auto overscroll-contain rounded-md border border-slate-200 bg-white shadow-lg">
          {loading && <div className="flex items-center justify-center gap-2 p-3 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Aranıyor…</div>}
          {!loading && results.length === 0 && <div className="p-3 text-center text-xs text-slate-400">{q.trim() ? "Eşleşen müşteri yok" : "Aramak için ad veya telefon yazın…"}</div>}
          {!loading && results.map((c) => (
            <button key={c.id} data-testid={`pos-customer-opt-${c.id}`} onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
              className="flex w-full items-center gap-2 border-b border-slate-50 px-3 py-2 text-left hover:bg-brand-green/5">
              <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${c.type === "corporate" ? "bg-sky-50 text-sky-600" : "bg-emerald-50 text-emerald-600"}`}><User className="h-3.5 w-3.5" /></div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800">{c.name}</p>
                <p className="truncate text-[11px] text-slate-400">{c.phone || "—"}{c.type === "corporate" ? " · Kurumsal" : ""}</p>
              </div>
              {c.can_wholesale && <span className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-brand-orange/10 px-1.5 py-0.5 text-[10px] font-bold text-brand-orange"><Tag className="h-2.5 w-2.5" /> Toptan</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default CustomerSearchSelect;
